import { useEffect, useState, useMemo } from "react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Search, Plus, Pencil, Trash2, Loader2, Inbox, AlertCircle, Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { formatRupiah, formatTanggal } from "../lib/format";
import { PageHeader } from "../components/ui/page-header";
import { EmptyState } from "../components/ui/empty-state";

const PAGE_SIZE = 10;
const today = () => new Date().toISOString().slice(0, 10);

export default function QCPage() {
    const { user, canDelete } = useAuth();
    const canWriteQC = user?.role === "admin" || user?.role === "staf_cek";
    const [items, setItems] = useState([]);
    const [setorPending, setSetorPending] = useState([]); // setor status_qc="belum_diperiksa"
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [fVendor, setFVendor] = useState("all");
    const [page, setPage] = useState(1);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [selectedSetorId, setSelectedSetorId] = useState("");
    const [selectedSetor, setSelectedSetor] = useState(null);
    const [tanggalQc, setTanggalQc] = useState(today());
    const [itemsQc, setItemsQc] = useState([]);
    const [catatanQc, setCatatanQc] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (fVendor !== "all") params.vendor_id = fVendor;
            const { data } = await api.get("/qc", { params });
            setItems(data);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setLoading(false); }
    };

    const loadPending = async () => {
        try {
            const [s, v] = await Promise.all([api.get("/setor-barang"), api.get("/vendors")]);
            setSetorPending(s.data.filter((x) => x.status_qc !== "selesai_qc"));
            setVendors(v.data);
        } catch (e) { toast.error(errorMessage(e)); }
    };

    useEffect(() => { loadPending(); }, []);
    useEffect(() => { const t = setTimeout(() => { load(); setPage(1); }, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, fVendor]);

    const vendorMap = useMemo(() => Object.fromEntries(vendors.map((v) => [v.id, v.nama])), [vendors]);
    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    const pageItems = useMemo(() => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [items, page]);

    const openCreate = () => {
        setEditing(null); setSelectedSetorId(""); setSelectedSetor(null);
        setTanggalQc(today()); setItemsQc([]); setCatatanQc(""); setDialogOpen(true);
        loadPending();
    };

    const openEdit = async (row) => {
        setEditing(row);
        setSelectedSetorId(row.setor_id);
        setTanggalQc(row.tanggal_qc);
        setCatatanQc(row.catatan_qc || "");
        setItemsQc(row.items_qc.map((i) => ({ ...i })));
        // fetch setor
        try {
            const { data } = await api.get(`/setor-barang`, { params: { spk_id: row.spk_id } });
            setSelectedSetor(data.find((s) => s.id === row.setor_id));
        } catch (e) { /* ignore */ }
        setDialogOpen(true);
    };

    const pickSetor = async (setor_id) => {
        setSelectedSetorId(setor_id);
        const s = setorPending.find((x) => x.id === setor_id);
        setSelectedSetor(s);
        if (s) {
            setItemsQc(s.items.map((si) => ({
                produk_id: si.produk_id,
                nama_produk_snapshot: si.nama_produk_snapshot,
                jumlah_setor: si.jumlah_pcs,
                jumlah_ok: si.jumlah_pcs,
                jumlah_reject_permanen: 0,
                jumlah_reject_bisa_diperbaiki: 0,
                harga_per_pcs_snapshot: si.harga_per_pcs_snapshot,
            })));
        }
    };

    const setItemQc = (idx, patch) => setItemsQc((arr) => arr.map((it, i) => i === idx ? { ...it, ...patch } : it));

    const perItemValid = itemsQc.map((it) => {
        const total = Number(it.jumlah_ok || 0) + Number(it.jumlah_reject_permanen || 0) + Number(it.jumlah_reject_bisa_diperbaiki || 0);
        const setor = Number(it.jumlah_setor || 0);
        return { total, setor, valid: total === setor };
    });
    const allValid = perItemValid.every((x) => x.valid) && itemsQc.length > 0;
    const nilaiPotongan = itemsQc.reduce((s, it) => s + Number(it.jumlah_reject_permanen || 0) * Number(it.harga_per_pcs_snapshot || 0), 0);

    const save = async () => {
        if (!selectedSetorId) return toast.error("Pilih setor barang");
        if (!allValid) return toast.error("Breakdown QC belum sesuai jumlah setor per produk");
        setSubmitting(true);
        try {
            const payload = {
                setor_id: selectedSetorId,
                tanggal_qc: tanggalQc,
                items_qc: itemsQc.map((it) => ({
                    produk_id: it.produk_id,
                    nama_produk_snapshot: it.nama_produk_snapshot,
                    jumlah_ok: parseInt(it.jumlah_ok) || 0,
                    jumlah_reject_permanen: parseInt(it.jumlah_reject_permanen) || 0,
                    jumlah_reject_bisa_diperbaiki: parseInt(it.jumlah_reject_bisa_diperbaiki) || 0,
                })),
                catatan_qc: catatanQc,
            };
            if (editing) {
                await api.put(`/qc/${editing.id}`, { tanggal_qc: tanggalQc, items_qc: payload.items_qc, catatan_qc: catatanQc });
                toast.success("QC diperbarui");
            } else {
                await api.post("/qc", payload);
                toast.success("QC berhasil dicatat");
            }
            setDialogOpen(false); load(); loadPending();
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSubmitting(false); }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try { await api.delete(`/qc/${confirmDelete.id}`); toast.success("QC dihapus"); setConfirmDelete(null); load(); loadPending(); }
        catch (e) { toast.error(errorMessage(e)); }
        finally { setDeleting(false); }
    };

    return (
        <div data-testid="qc-page">
            <PageHeader
                title="Pemeriksaan QC"
                description="Verifikasi kualitas setor barang jadi. Breakdown OK / Reject Permanen / Reject Bisa Diperbaiki."
                actions={canWriteQC && (
                    <Button onClick={openCreate} data-testid="qc-add-button" className="rounded-lg gap-2 text-white font-medium" style={{ background: "var(--apple-blue)" }}>
                        <Plus className="w-4 h-4" /> Buat QC
                    </Button>
                )}
            />

            <div className="rounded-xl bg-white p-4 mb-4" style={{ border: "1px solid var(--border)" }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
                        <Input placeholder="Cari nomor QC..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="qc-search-input" className="pl-9 rounded-lg h-10" />
                    </div>
                    <Select value={fVendor} onValueChange={setFVendor}>
                        <SelectTrigger className="rounded-lg h-10"><SelectValue placeholder="Semua vendor" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua vendor</SelectItem>
                            {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.nama}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden space-y-2">
                {loading ? (
                    <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--apple-blue)" }} /></div>
                ) : pageItems.length === 0 ? (
                    <div className="rounded-xl bg-white" style={{ border: "1px solid var(--border)" }}>
                        <EmptyState icon={ShieldCheck} title="Belum ada QC" description="Belum ada pemeriksaan QC." testid="qc-empty-mobile" />
                    </div>
                ) : pageItems.map((row) => {
                    const ok = row.items_qc?.reduce((s, i) => s + Number(i.jumlah_ok || 0), 0) || 0;
                    const rp = row.items_qc?.reduce((s, i) => s + Number(i.jumlah_reject_permanen || 0), 0) || 0;
                    const rbp = row.items_qc?.reduce((s, i) => s + Number(i.jumlah_reject_bisa_diperbaiki || 0), 0) || 0;
                    return (
                        <div key={row.id} className="rounded-xl bg-white p-4" style={{ border: "1px solid var(--border)" }} data-testid={`qc-card-${row.id}`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="min-w-0 flex-1">
                                    <div className="font-semibold text-sm" style={{ color: "var(--apple-blue)" }}>{row.nomor_qc}</div>
                                    <div className="text-xs truncate" style={{ color: "var(--text-primary)" }}>{vendorMap[row.vendor_id] || "-"}</div>
                                    <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>Setor: {row.nomor_setor_snapshot}</div>
                                </div>
                                <div className="text-xs" style={{ color: "var(--text-muted)" }}>{formatTanggal(row.tanggal_qc)}</div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs mt-3 pt-3" style={{ borderTop: "1px solid var(--border-soft)" }}>
                                <div><div style={{ color: "var(--text-muted)" }}>OK</div><div className="font-semibold text-emerald-700 num-tabular">{ok}</div></div>
                                <div><div style={{ color: "var(--text-muted)" }}>Permanen</div><div className="font-semibold text-rose-700 num-tabular">{rp}</div></div>
                                <div><div style={{ color: "var(--text-muted)" }}>Perbaikan</div><div className="font-semibold text-amber-700 num-tabular">{rbp}</div></div>
                            </div>
                            <div className="flex items-center justify-between pt-3 mt-3" style={{ borderTop: "1px solid var(--border-soft)" }}>
                                <div className="text-xs"><span style={{ color: "var(--text-muted)" }}>Potongan: </span><span className="font-semibold num-tabular" style={{ color: "var(--text-primary)" }}>{formatRupiah(row.nilai_potongan || 0)}</span></div>
                                <div className="flex items-center gap-1">
                                    {canWriteQC && <button onClick={() => openEdit(row)} data-testid={`qc-edit-btn-${row.id}`} className="p-2 rounded-lg hover:bg-[var(--bg-subtle)]" style={{ color: "var(--text-secondary)" }}><Pencil className="w-4 h-4" /></button>}
                                    {canDelete() && <button onClick={() => setConfirmDelete(row)} data-testid={`qc-delete-btn-${row.id}`} className="p-2 rounded-lg hover:bg-[var(--apple-red-soft)]" style={{ color: "var(--apple-red)" }}><Trash2 className="w-4 h-4" /></button>}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block rounded-xl overflow-hidden bg-white" style={{ border: "1px solid var(--border)" }}>
                <Table data-testid="qc-data-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Nomor QC</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Setor</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Vendor</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Tanggal</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider text-right" style={{ color: "var(--text-muted)" }}>OK</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider text-right" style={{ color: "var(--text-muted)" }}>Rej.Permanen</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider text-right" style={{ color: "var(--text-muted)" }}>Rej.Perbaikan</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider text-right" style={{ color: "var(--text-muted)" }}>Potongan</TableHead>
                            <TableHead className="text-right text-xs font-semibold uppercase tracking-wider w-28" style={{ color: "var(--text-muted)" }}>Aksi</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={9} className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: "var(--apple-blue)" }} /></TableCell></TableRow>
                        ) : pageItems.length === 0 ? (
                            <TableRow><TableCell colSpan={9}><EmptyState icon={ShieldCheck} title="Belum ada QC" description="Belum ada pemeriksaan QC." testid="qc-empty-desktop" /></TableCell></TableRow>
                        ) : pageItems.map((row) => {
                            const ok = row.items_qc?.reduce((s, i) => s + Number(i.jumlah_ok || 0), 0) || 0;
                            const rp = row.items_qc?.reduce((s, i) => s + Number(i.jumlah_reject_permanen || 0), 0) || 0;
                            const rbp = row.items_qc?.reduce((s, i) => s + Number(i.jumlah_reject_bisa_diperbaiki || 0), 0) || 0;
                            return (
                                <TableRow key={row.id} data-testid={`qc-row-${row.id}`}>
                                    <TableCell className="font-semibold" style={{ color: "var(--apple-blue)" }}>{row.nomor_qc}</TableCell>
                                    <TableCell className="text-sm" style={{ color: "var(--text-primary)" }}>{row.nomor_setor_snapshot}</TableCell>
                                    <TableCell className="text-sm" style={{ color: "var(--text-primary)" }}>{vendorMap[row.vendor_id] || "-"}</TableCell>
                                    <TableCell className="text-sm" style={{ color: "var(--text-muted)" }}>{formatTanggal(row.tanggal_qc)}</TableCell>
                                    <TableCell className="text-sm text-emerald-700 font-semibold text-right num-tabular">{ok}</TableCell>
                                    <TableCell className="text-sm text-rose-700 font-semibold text-right num-tabular">{rp}</TableCell>
                                    <TableCell className="text-sm text-amber-700 font-semibold text-right num-tabular">{rbp}</TableCell>
                                    <TableCell className="text-sm font-semibold text-right num-tabular" style={{ color: "var(--text-primary)" }}>{formatRupiah(row.nilai_potongan || 0)}</TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            {canWriteQC && <button onClick={() => openEdit(row)} data-testid={`qc-edit-btn-${row.id}`} className="p-2 rounded-lg hover:bg-[var(--bg-subtle)]" style={{ color: "var(--text-secondary)" }}><Pencil className="w-4 h-4" /></button>}
                                            {canDelete() && <button onClick={() => setConfirmDelete(row)} data-testid={`qc-delete-btn-${row.id}`} className="p-2 rounded-lg hover:bg-[var(--apple-red-soft)]" style={{ color: "var(--apple-red)" }}><Trash2 className="w-4 h-4" /></button>}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                {!loading && items.length > 0 && (
                    <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: "1px solid var(--border)", background: "var(--bg-subtle)" }}>
                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>Menampilkan {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, items.length)} dari {items.length} data</div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-lg h-8">Sebelumnya</Button>
                            <span className="text-xs px-2" style={{ color: "var(--text-secondary)" }}>Hal {page}/{totalPages}</span>
                            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="rounded-lg h-8">Selanjutnya</Button>
                        </div>
                    </div>
                )}
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="qc-modal-dialog" className="md:rounded-2xl md:max-w-3xl md:max-h-[90vh] md:overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit QC" : "Buat Pemeriksaan QC"}</DialogTitle>
                        <DialogDescription>Untuk tiap produk: jumlah OK + Reject Permanen + Reject Bisa Diperbaiki HARUS sama dengan jumlah setor.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label>Setor Barang <span className="text-rose-600">*</span></Label>
                                <Select value={selectedSetorId} onValueChange={pickSetor} disabled={!!editing}>
                                    <SelectTrigger data-testid="qc-form-setor" className="mt-1.5 rounded-xl"><SelectValue placeholder="Pilih setor yang belum di-QC" /></SelectTrigger>
                                    <SelectContent>
                                        {setorPending.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">Semua setor sudah di-QC</div>}
                                        {setorPending.map((s) => (
                                            <SelectItem key={s.id} value={s.id}>
                                                {s.nomor_setor} · {vendorMap[s.vendor_id]} · {formatTanggal(s.tanggal_setor)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Tanggal QC <span className="text-rose-600">*</span></Label>
                                <Input type="date" value={tanggalQc} onChange={(e) => setTanggalQc(e.target.value)} data-testid="qc-form-tanggal" className="mt-1.5 rounded-xl" />
                            </div>
                        </div>

                        {selectedSetor && (
                            <div className="p-3 rounded-xl bg-teal-50/40 border border-teal-200/60 text-xs text-slate-600">
                                <span className="font-semibold text-teal-700">{selectedSetor.nomor_setor}</span> · SPK {selectedSetor.nomor_spk_snapshot} · Total nilai {formatRupiah(selectedSetor.total_nilai)}
                            </div>
                        )}

                        {itemsQc.length > 0 && (
                            <div>
                                <Label>Breakdown per Produk</Label>
                                <div className="space-y-2 mt-2">
                                    {itemsQc.map((it, idx) => {
                                        const v = perItemValid[idx];
                                        return (
                                            <div key={it.produk_id} data-testid={`qc-form-item-${idx}`} className={`p-3 rounded-xl border ${v.valid ? "bg-emerald-50/40 border-emerald-200/70" : "bg-rose-50/40 border-rose-200/70"}`}>
                                                <div className="flex items-center justify-between gap-2 mb-2">
                                                    <div className="text-sm font-semibold text-slate-900">{it.nama_produk_snapshot}</div>
                                                    <div className="text-xs text-slate-600 num-tabular">Setor: <span className="font-bold">{it.jumlah_setor} pcs</span></div>
                                                </div>
                                                <div className="grid grid-cols-3 gap-2">
                                                    <div>
                                                        <div className="text-[10px] uppercase text-emerald-700 mb-1 font-semibold">OK</div>
                                                        <Input type="number" min="0" value={it.jumlah_ok} data-testid={`qc-form-ok-${idx}`} onChange={(e) => setItemQc(idx, { jumlah_ok: e.target.value })} className="rounded-lg h-9 bg-white" />
                                                    </div>
                                                    <div>
                                                        <div className="text-[10px] uppercase text-rose-700 mb-1 font-semibold">Reject Permanen</div>
                                                        <Input type="number" min="0" value={it.jumlah_reject_permanen} data-testid={`qc-form-permanen-${idx}`} onChange={(e) => setItemQc(idx, { jumlah_reject_permanen: e.target.value })} className="rounded-lg h-9 bg-white" />
                                                    </div>
                                                    <div>
                                                        <div className="text-[10px] uppercase text-amber-700 mb-1 font-semibold">Bisa Diperbaiki</div>
                                                        <Input type="number" min="0" value={it.jumlah_reject_bisa_diperbaiki} data-testid={`qc-form-perbaikan-${idx}`} onChange={(e) => setItemQc(idx, { jumlah_reject_bisa_diperbaiki: e.target.value })} className="rounded-lg h-9 bg-white" />
                                                    </div>
                                                </div>
                                                <div className={`text-xs mt-2 flex items-center gap-1.5 ${v.valid ? "text-emerald-700" : "text-rose-700"}`}>
                                                    {v.valid ? <Check className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                                                    Total breakdown: <span className="font-semibold num-tabular">{v.total} pcs</span> / setor {v.setor} pcs
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="mt-3 text-right text-sm">
                                    <span className="text-slate-600">Nilai Potongan (reject permanen): </span>
                                    <span data-testid="qc-form-nilai-potongan" className="font-bold text-rose-700 num-tabular">{formatRupiah(nilaiPotongan)}</span>
                                </div>
                            </div>
                        )}

                        <div>
                            <Label>Catatan QC</Label>
                            <Textarea value={catatanQc} onChange={(e) => setCatatanQc(e.target.value)} className="mt-1.5 rounded-xl" rows={2} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Batal</Button>
                        <Button onClick={save} disabled={submitting || !allValid} data-testid="qc-modal-save-button" className="rounded-lg text-white" style={{ background: "var(--apple-blue)" }}>
                            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menyimpan...</> : "Simpan QC"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="md:rounded-2xl">
                    <AlertDialogHeader><AlertDialogTitle>Hapus QC</AlertDialogTitle><AlertDialogDescription>Yakin hapus <span className="font-semibold">{confirmDelete?.nomor_qc}</span>? Setor akan kembali ke status belum diperiksa.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-lg">Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} disabled={deleting} data-testid="confirm-delete-button" className="rounded-lg text-white" style={{ background: "var(--apple-red)" }}>{deleting ? "..." : "Hapus"}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
